/* Filter & Merge — date / service / invoice filters for Bulk Data merge.
 * Filters are remembered per user (localStorage). */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Filter, Loader2, RefreshCw, Search } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCOPES = [["today", "Today"], ["yesterday", "Yesterday"], ["today_yesterday", "Today + Yesterday"],
  ["last7", "Last 7 days"], ["custom", "Custom"]];
const todayIST = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const keyFor = (user) => `bdMergeFilters:${user?.email || user?.id || "me"}`;

const Pill = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${on
            ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
    {children}
  </button>
);

export const BulkMergeFilterDialog = ({ open, onOpenChange, initialRegion, user, runMerge }) => {
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(keyFor(user)) || "{}"); } catch { return {}; }
  }, [user]);
  const [region, setRegion] = useState(initialRegion || "usa");
  const [scope, setScope] = useState(saved.scope || "today");
  const [from, setFrom] = useState(saved.from || todayIST());
  const [to, setTo] = useState(saved.to || todayIST());
  const [services, setServices] = useState(saved.services || []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setRegion(initialRegion || "usa"); }, [open, initialRegion]);
  useEffect(() => {
    localStorage.setItem(keyFor(user), JSON.stringify({ scope, from, to, services }));
  }, [user, scope, from, to, services]);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const qs = new URLSearchParams({ scope, ...(scope === "custom" ? { date_from: from, date_to: to } : {}) });
    axios.get(`${API}/bulk-data/merge/filter-candidates?${qs}`, { headers: getAuthHeader() })
      .then(r => setRows(r.data || []))
      .catch(e => { toast.error(e.response?.data?.detail || "Failed to load invoices"); setRows([]); })
      .finally(() => setLoading(false));
  }, [open, scope, from, to]);

  const inRegion = rows.filter(r => r.region === region);
  const allServices = [...new Set(inRegion.map(r => r.service))].sort();
  const activeSvcs = services.filter(s => allServices.includes(s));
  const visible = inRegion.filter(r => (!activeSvcs.length || activeSvcs.includes(r.service)) &&
    (!q.trim() || `${r.invoice_no} ${r.filename} ${r.sender_company}`.toLowerCase().includes(q.trim().toLowerCase())));
  // Default: tick today's invoices (or everything when the window has no today rows).
  useEffect(() => {
    const t = todayIST();
    const hasToday = inRegion.some(r => r.date_ist === t);
    setChecked(Object.fromEntries(inRegion.map(r => [r.id, !hasToday || r.date_ist === t])));
  }, [rows, region]); // eslint-disable-line
  const picked = visible.filter(r => checked[r.id]);
  const totalRows = picked.reduce((s, r) => s + (r.total_rows || 0), 0);
  const toggleSvc = (s) => setServices(v => v.includes(s) ? v.filter(x => x !== s) : [...v, s]);
  const setAll = (on) => setChecked(Object.fromEntries(visible.map(r => [r.id, on])));

  const run = async () => {
    setBusy(true);
    const ok = await runMerge({ region, ids: picked.map(r => r.id) });
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] w-[900px] max-h-[92vh] overflow-y-auto" data-testid="merge-filter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Filter className="w-4 h-4 text-indigo-600" />Filter &amp; Merge</DialogTitle>
          <DialogDescription className="text-xs">
            One Excel with continuous SR, a SERVICE column and a Merge Summary sheet. USA keeps its own ArticleDetails + SubPieces format.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-500 w-16">Region</span>
            <Pill on={region === "usa"} onClick={() => setRegion("usa")} testId="merge-filter-region-usa">USA</Pill>
            <Pill on={region === "other"} onClick={() => setRegion("other")} testId="merge-filter-region-other">Other countries</Pill>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-500 w-16">Date</span>
            {SCOPES.map(([k, l]) => <Pill key={k} on={scope === k} onClick={() => setScope(k)} testId={`merge-filter-scope-${k}`}>{l}</Pill>)}
            {scope === "custom" && (
              <span className="flex items-center gap-1 text-xs">
                <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 w-36 text-xs" data-testid="merge-filter-from" />
                –
                <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 w-36 text-xs" data-testid="merge-filter-to" />
              </span>)}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-500 w-16">Service</span>
            <Pill on={!activeSvcs.length} onClick={() => setServices([])} testId="merge-filter-service-all">All</Pill>
            {allServices.map(s => (
              <Pill key={s} on={activeSvcs.includes(s)} onClick={() => toggleSvc(s)} testId={`merge-filter-service-${s}`}>
                {s} <span className="opacity-60">{inRegion.filter(r => r.service === s).length}</span>
              </Pill>))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-56">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invoice / file / sender"
                     className="h-7 pl-7 text-xs" data-testid="merge-filter-search" />
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAll(true)} data-testid="merge-filter-select-all">Select all</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAll(false)} data-testid="merge-filter-clear">Clear</Button>
          </div>
          <div className="border rounded-lg max-h-[42vh] overflow-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" />Loading…</div>
            ) : !visible.length ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="merge-filter-empty">No {region === "usa" ? "USA" : "other-country"} bulk-data entries for these filters.</div>
            ) : (
              <table className="w-full text-xs" data-testid="merge-filter-table">
                <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase text-slate-500">
                  <tr>{["", "Invoice", "Service", "Date", "File", "Rows"].map((h, i) => <th key={i} className="py-1.5 px-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id} className={`border-b ${checked[r.id] ? "bg-indigo-50/50" : ""}`} data-testid={`merge-filter-row-${r.id}`}>
                      <td className="py-1 px-2"><input type="checkbox" checked={!!checked[r.id]}
                        onChange={() => setChecked(c => ({ ...c, [r.id]: !c[r.id] }))} data-testid={`merge-filter-check-${r.id}`} /></td>
                      <td className="py-1 px-2 font-mono">{r.invoice_no || "—"}</td>
                      <td className="py-1 px-2"><span className="px-1.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold">{r.service}</span></td>
                      <td className="py-1 px-2 text-slate-500 whitespace-nowrap">{r.date_ist}</td>
                      <td className="py-1 px-2 truncate max-w-[300px]" title={r.filename}>{r.filename}</td>
                      <td className="py-1 px-2 text-center">{r.total_rows || 0}</td>
                    </tr>))}
                </tbody>
              </table>)}
          </div>
        </div>
        <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between">
          <span className="text-xs text-slate-600" data-testid="merge-filter-summary">
            {picked.length} invoice(s) · {totalRows} rows · {new Set(picked.map(r => r.service)).size} service(s)
          </span>
          <Button onClick={run} disabled={busy || !picked.length} data-testid="merge-filter-run"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Merge {picked.length} to Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
